import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot, Root } from 'react-dom/client';
import { getPortalAlias } from './Services/common';
import { TCustomAdminConfig, ICustomAdminConfigMap } from './Types/custom';
import { ICustomFrontEndConfigMap, TCustomFrontEndConfig } from './Types/customFrontEnd';

//moving queryClient to FrontEndReact causes problems because they use different react-query-library locations.
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: false,
		},
	},
});

// Listen for cache invalidation requests from legacy AngularJS code.
// Dispatch a 'BizInvalidateReactQuery' CustomEvent with { detail: { queryKeys: [['KEY', id], ...] } }
// to invalidate React Query caches from outside React.
document.addEventListener('BizInvalidateReactQuery', ((e: CustomEvent<{ queryKeys: unknown[][] }>) => {
	const keys = e.detail?.queryKeys;
	if (keys && Array.isArray(keys)) {
		keys.forEach((key) => {
			queryClient.invalidateQueries({ queryKey: key });
		});
	}
}) as EventListener);

export function AddBizReactLoader(
	root: Root | undefined,
	rootId: string,
	getApp: (e: CustomEvent) => React.ReactNode | Promise<React.ReactNode>
) {
	const loadEventName = `Load-${rootId}`;
	document.addEventListener(loadEventName, ((e: CustomEvent) => {
		console.log(loadEventName, root);
		if (!root) {
			const container = document.getElementById(rootId);
			root = createRoot(container!);
		}
		try {
			const appResult = getApp(e);
			if (appResult instanceof Promise) {
				console.log('AddBizReactLoader app is a Promise');
				appResult
					.then((app) => {
						root!.render(
							<QueryClientProvider client={queryClient}>
								{app}
								<ReactQueryDevtools initialIsOpen={false} />
							</QueryClientProvider>
						);
					})
					.catch((error) => {
						console.error('Error loading Biz React App:', error);
					});
			} else {
				const app = appResult;
				root.render(
					<QueryClientProvider client={queryClient}>
						{app}
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				);
			}
		} catch (error) {
			console.error('Error loading Biz React App:', error);
		}
	}) as EventListener);

	const unloadEventName = `Unload-${rootId}`;
	document.addEventListener(unloadEventName, () => {
		console.log(unloadEventName, root);
		if (root) {
			root.unmount();
			root = undefined;
		}
	});
}

export async function GetCustomAdminConfig<T extends keyof ICustomAdminConfigMap>(
	configType: T
): Promise<ICustomAdminConfigMap[T] | undefined> {
	try {
		const { configs }: { configs: TCustomAdminConfig[] } = await import(
			`customers/${getPortalAlias()}/React/Admin/customComponentIndex.ts`
		);
		if (!configs) return undefined;

		const config = configs.find((c) => c.type === configType);
		if (!config) return undefined;
		return config?.config as ICustomAdminConfigMap[T];
	} catch (error) {
		console.error('Error loading custom admin config:', error);
		return undefined;
	}
}

export async function GetCustomFrontEndConfig<T extends keyof ICustomFrontEndConfigMap>(
	configType: T
): Promise<ICustomFrontEndConfigMap[T] | undefined> {
	try {
		const { configs }: { configs: TCustomFrontEndConfig[] } = await import(
			`customers/${getPortalAlias()}/React/FrontEnd/customComponentIndex.ts`
		);
		if (!configs) return undefined;

		const config = configs.find((c) => c.type === configType);
		if (!config) return undefined;
		return config?.config as ICustomFrontEndConfigMap[T];
	} catch (error) {
		console.error('Error loading custom frontend config:', error);
		return undefined;
	}
}

export async function GetCustomFrontEndConfigs(): Promise<TCustomFrontEndConfig[]> {
	try {
		const { configs }: { configs: TCustomFrontEndConfig[] } = await import(
			`customers/${getPortalAlias()}/React/FrontEnd/customComponentIndex.ts`
		);
		return configs ?? [];
	} catch (error) {
		console.error('Error loading custom frontend configs:', error);
		return [];
	}
}
